import * as React from 'react';

import { DataGrid, ListScreenLayout, Pagination } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import {
  agentActivateMutationAtom,
  agentDeactivateMutationAtom,
} from '../../_graphql/mutationAgentActivate';
import { AgentRecord } from '../../_graphql/queryAgentList';
import * as state from '../../_state/agentList.state';
import { useTranslations } from '../../i18n/useTranslations';

import { AgentFilterBar } from './AgentFilterBar';
import { useAgentsColumns } from './hooks/useAgentsColumns';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

//
// ⚛️ ⚛️ ⚛️ AGENTS LIST VIEW ⚛️ ⚛️ ⚛️
//
export const AgentsListView = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();

  const [limit = 25, setLimit] = useAtom(state.$limit);
  const [offset, setOffset] = useAtom(state.$offset);
  useAtom(state.$typeFilter);
  const { data: results, isLoading, error } = useAtomValue(state.$agents);

  const toast = useToast();
  const activateMutation = agentActivateMutationAtom({
    onSuccess: (get) => {
      get(state.$agentsRaw).refetch();
      toast.success(t.aiManagement.agents.notifications.activated);
    },
  });
  const deactivateMutation = agentDeactivateMutationAtom({
    onSuccess: (get) => {
      get(state.$agentsRaw).refetch();
      toast.success(t.aiManagement.agents.notifications.deactivated);
    },
  });
  const activate = useSetAtom(activateMutation);
  const deactivate = useSetAtom(deactivateMutation);

  const agents = React.useMemo(() => results?.agents ?? [], [results?.agents]);
  const total = results?.total ?? 0;

  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );

  const handleActivateToggle = React.useCallback(
    async (agent: AgentRecord) => {
      try {
        if (agent.isActive) {
          await deactivate({ id: agent.id });
        } else {
          await activate({ id: agent.id });
        }
      } catch (_error) {
        toast.error(t.aiManagement.agents.notifications.error);
      }
    },
    [activate, deactivate, toast, t],
  );

  const columns = useAgentsColumns({
    onActivateToggle: handleActivateToggle,
    navigate,
  });

  return (
    <ListScreenLayout
      filters={<AgentFilterBar />}
      footer={
        total > limit ? (
          <Pagination
            limit={limit}
            offset={offset}
            total={total}
            onChange={handlePaginationChange}
          />
        ) : undefined
      }
      className="@AgentsListView"
    >
      <DataGrid<AgentRecord>
        columns={columns}
        dataSource={agents}
        rowKey="id"
        fullWidth
        isLoading={isLoading}
        hasError={!!error}
        errorPanelProps={
          error
            ? {
                description: error.message,
                centered: true,
              }
            : undefined
        }
        emptyPanelProps={{
          title: t.aiManagement.agents.empty.title,
          description: t.aiManagement.agents.empty.description,
        }}
      />
    </ListScreenLayout>
  );
};
